import React, { useState, useEffect } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import {
    Col, InfoBox, Button,
    Input, Label, ToggleSwitch
} from '@evertel/web/ui'
import { itemProps, SchemaFieldItemProps } from '../data/_schemaItemProps'
import OptionsBuilder, { Option } from './OptionsBuilder'
import { FileTypesSelector } from './FileTypesSelector'
import { SchemaMediaController } from '../../../controller/medias/SchemaMediaController'
import TextTypeSelector from './TextTypeSelector'

interface ItemEditFormProps {
    field: SchemaFieldItemProps | null  // The original field data
    onChange: (props: any) => void
    onChangeListLayoutField: (props: any) => void
    /* callback will be resolved in SchemaBuilderFull */
    onAction: (action: string, props: any) => void
    schemaMediaController: SchemaMediaController
}

const ItemEditForm: React.FC<ItemEditFormProps> = ({
    field: initialField,
    onChange,
    onChangeListLayoutField,
    onAction,
    schemaMediaController
}) => {
    const [editedField, setEditedField] = useState<SchemaFieldItemProps>({} as SchemaFieldItemProps)
    const [fieldConfig, setFieldConfig] = useState<any>(null)
    const [isValid, setIsValid] = useState(false)

    // First useEffect to validate the initial field and set config
    useEffect(() => {
        if (!initialField) {
            setIsValid(false)
            return
        }

        const config = itemProps[initialField.type]
        if (!config) {
            console.error(`No matching config for ${initialField.type}`)
            setIsValid(false)
            return
        }

        setFieldConfig(config)
        setIsValid(true)
    }, [initialField])

    // Second useEffect to initialize editable values from the initial field data
    useEffect(() => {
        if (!isValid || !initialField || !fieldConfig) return

        const initializedValues = {} as SchemaFieldItemProps

        // map config to state and populate with initial field data
        for (const [key, value] of Object.entries(fieldConfig) as [string, any][]) {
            switch (value?.type) {
                case 'string':
                    initializedValues[key] = initialField[key] ?? ''
                    break
                case 'number':
                    // Ensure we keep 0 values and convert empty/null to 0 or empty string
                    initializedValues[key] = initialField[key] ?? ''
                    break
                case 'boolean':
                    // Use nullish coalescing to check for default, then false
                    initializedValues[key] = initialField[key] ?? fieldConfig[key].default ?? false
                    break
                case 'options':
                    // Ensure we always have an array, even if empty
                    initializedValues[key] = Array.isArray(initialField[key]) ? initialField[key] : []
                    break
                default:
                    initializedValues[key] = initialField[key] ?? ''
                    break
            }
        }
        setEditedField(initializedValues)
    }, [initialField, fieldConfig, isValid])

    // Third useEffect to notify parent of changes
    useEffect(() => {
        if (!onChange || !isValid || !initialField) return

        if (initialField.type === 'list' || initialField.internalSchemaId) {
            onChangeListLayoutField({
                [initialField.id]: {
                    ...editedField,
                    internalSchemaId: initialField.internalSchemaId
                }
            })
        } else {
            onChange({
                [initialField.id]: editedField
            })
        }
    }, [editedField])

    const handleFieldChange = (fieldName: string, value: any) => {
        setEditedField((prev) => ({
            ...prev,
            [fieldName]: value
        }))
    }

    const renderFormField = ({ data, ...otherProps }: { data: SchemaFieldItemProps }) => {
        const optional = (data?.label?.search('(optional)') !== -1) ? true : false
        const label = (optional) ? data.label?.replace('(optional)', '') : data.label

        switch (data?.type) {
            case 'string':
            case 'number':
                return (
                    <Col {...otherProps}>
                        <Label
                            text={label}
                            htmlFor={`form-field-${data.id}`}
                            optional={optional}
                            hint={data.hint}
                            hintPosition="right"
                        />
                        <Input
                            type={(data.type === 'string') ? 'text' : data.type}
                            id={`form-field-${data.id}`}
                            value={editedField[data.id]}
                            onChange={(e) => handleFieldChange(data.id, e.target.value)}
                            maxLength={data.maxLength}
                        />
                    </Col>
                )
            case 'boolean':
                if (data.id === 'multiline' && initialField?.id === 'title') return null
                return (
                    <ToggleSwitch
                        id={data.id}
                        label={label}
                        checked={editedField[data.id]}
                        onChange={(checked) => handleFieldChange(data.id, checked)}
                        {...otherProps}
                    />
                )
            case 'options':
                // eslint-disable-next-line no-case-declarations
                const groupLabelId = `options-${initialField.id}-label`
                return (
                    <Col {...otherProps}>
                        <Label
                            text={label}
                            hint={data.hint}
                            htmlFor={`${groupLabelId}`}
                        />
                        <div role="group">
                            <OptionsBuilder
                                id={`${groupLabelId}`}
                                options={editedField.options}
                                isCheckbox={initialField?.type === 'checkbox' || initialField?.type === 'checkbox-group'}
                                onChange={(options) => handleFieldChange(data.id, options)}
                                schemaMediaController={schemaMediaController}
                            />
                        </div>
                    </Col>
                )
            case 'contentType':
                if (editedField.multiline || initialField?.id === 'title') return null
                return (
                    <TextTypeSelector
                        label={label}
                        onChange={(types) => handleFieldChange(data.id, types)}
                        value={editedField[data.id]}
                    />
                )
            case 'fileTypes':
                return (
                    <FileTypesSelector
                        label={label}
                        typesAllowed={editedField[data.id]}
                        onChange={(types) => handleFieldChange(data.id, types)}
                    />
                )
            default:
                return null
        }
    }

    if (!isValid) {
        return null
    }

    // convert config object to mappable array
    const fieldProps = Object.keys(fieldConfig).map(key => ({ id: key, ...fieldConfig[key] }))

    return (
        <PerfectScrollbar className="pe-3">
            <FieldInfoBox
                field={initialField}
            />
            {fieldProps?.map((f, idx) =>
                <div
                    key={idx}
                    className="mb-3">
                    {renderFormField({
                        data: f
                    })}
                </div>
            )}
            {(initialField?.type === 'list') &&
                <Button
                    color="link"
                    className="mt-3"
                    onClick={() => onAction('open-form', {sKey: initialField.id})}>
                    Edit form fields
                </Button>
            }
        </PerfectScrollbar>
    )
}

const FieldInfoBox = ({
    field
}: { field: SchemaFieldItemProps }) => {

    // if (field.id === 'title') {
    //     return (
    //         <InfoBox color="info">
    //             The "title" field cannot be deleted. The value of this field will be used as the name of each document created with this template.
    //         </InfoBox>
    //     )
    // }
    if (field.type === 'list') {
        return (
            <InfoBox color="info">
                A "Sub Form" is a form that is inserted in another form.
                Useful for elements that can include one or many entries (i.e. Suspects, Vehicles, etc.)
            </InfoBox>
        )
    }
    return null
}

export {
    ItemEditForm
}
